import React, { useCallback, useEffect, useMemo, useState } from "react";
import HelpBubble from "../pricing/HelpBubble";
import { SENDERS_HELP } from "../pricing/helpTexts";
import { getMarketplaceSenders, saveMarketplaceSenders } from "../../../api/marketplaceSendersApi";
import { formatApiError } from "../pricing/pricingFormat";
import "../pricing/marketplace-pricing.css";

// Поля, которые таблица редактирует. Остальное (доп. наценка, ярус) сервер не трогает.
export const EDITABLE_FIELDS = [
    "is_active",
    "priority_override",
    "min_markup_threshold",
    "is_rrp",
    "dumping_mode",
    "order_ttn_mode",
];

const TTN_LABELS = { auto: "автоматически", manual_later: "позже вручную" };

const effectiveTtn = (row) => row.order_ttn_mode || row.supplier_ttn_mode || "auto";

const toDraft = (items) =>
    (items || []).map((row) => ({ ...row }));

export const buildChangedItems = (draft, original) => {
    const byCode = new Map((original || []).map((row) => [row.supplier_code, row]));
    return draft
        .map((row) => {
            const before = byCode.get(row.supplier_code) || {};
            const changes = {};
            EDITABLE_FIELDS.forEach((field) => {
                if ((row[field] ?? null) !== (before[field] ?? null)) changes[field] = row[field] ?? null;
            });
            return Object.keys(changes).length ? { supplier_code: row.supplier_code, ...changes } : null;
        })
        .filter(Boolean);
};

function NumberCell({ row, field, label, placeholder, disabled, onChange, step = "1" }) {
    return (
        <input
            className="mp-input mp-num-input"
            type="number"
            min="0"
            step={step}
            aria-label={`${label}: ${row.supplier_name || row.supplier_code}`}
            placeholder={placeholder}
            value={row[field] ?? ""}
            disabled={disabled}
            onChange={(event) => onChange(row.supplier_code, field, event.target.value === "" ? null : Number(event.target.value))}
        />
    );
}

export default function MarketplaceSendersTable({ platform, accountId, active = true, defaultProfit = null, onSaved }) {
    const [original, setOriginal] = useState([]);
    const [draft, setDraft] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [onlyActive, setOnlyActive] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!accountId || !active) return;
        setLoading(true);
        setError("");
        try {
            const data = await getMarketplaceSenders(platform, accountId);
            setOriginal(data.items || []);
            setDraft(toDraft(data.items));
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось загрузить отправителей."));
        } finally {
            setLoading(false);
        }
    }, [platform, accountId, active]);

    useEffect(() => {
        load();
    }, [load]);

    const changed = useMemo(() => buildChangedItems(draft, original), [draft, original]);
    const dirty = changed.length > 0;

    const change = (code, field, value) => {
        setDraft((rows) => rows.map((row) => (row.supplier_code === code ? { ...row, [field]: value } : row)));
        setMessage("");
        setError("");
    };

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        setError("");
        setMessage("");
        try {
            const data = await saveMarketplaceSenders(platform, accountId, changed);
            setOriginal(data.items || []);
            setDraft(toDraft(data.items));
            setMessage("Настройки отправителей сохранены.");
            if (onSaved) onSaved(data.items || []);
        } catch (apiError) {
            const timedOut = apiError?.code === "ECONNABORTED" || apiError?.code === "ETIMEDOUT";
            setError(
                timedOut
                    ? "Сервер не ответил за 20 секунд. Обновите список и проверьте результат перед повтором."
                    : formatApiError(apiError, "Не удалось сохранить. Изменения оставлены в форме."),
            );
        } finally {
            setSaving(false);
        }
    };

    const profitPlaceholder =
        defaultProfit !== null && defaultProfit !== undefined && defaultProfit !== ""
            ? `по умолч. ${Number(defaultProfit).toLocaleString("uk-UA")}`
            : "по умолч.";
    const rows = onlyActive ? draft.filter((row) => row.is_active) : draft;
    const disabled = saving || loading;

    if (!accountId) {
        return (
            <div className="mp-pricing">
                <div className="mp-callout">Сначала выберите или создайте аккаунт.</div>
            </div>
        );
    }

    return (
        <div className="mp-pricing">
            <section className="mp-card" aria-label="Отправители">
                <HelpBubble title="Отправители площадки">{SENDERS_HELP}</HelpBubble>
                <div className="mp-card-head">
                    <div>
                        <h3 className="mp-title">Отправители</h3>
                        <p className="mp-lead">
                            Какие поставщики участвуют на площадке и с какими настройками. Пустое поле — значение
                            наследуется (подсказка серым). Цены считает вкладка «Цены».
                        </p>
                    </div>
                </div>
                <div className="mp-actions">
                    <button type="button" className="mp-btn mp-btn-primary" disabled={!dirty || disabled} onClick={save}>
                        {saving ? "Сохраняем…" : "Сохранить"}
                    </button>
                    {dirty && !saving ? <span className="mp-muted">Есть несохранённые изменения ({changed.length})</span> : null}
                    {loading ? <span className="mp-muted" role="status">Загрузка…</span> : null}
                    <label className="mp-check" style={{ marginLeft: "auto" }}>
                        <input type="checkbox" checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} />
                        Только активные
                    </label>
                </div>
                {error ? <div className="mp-callout mp-callout-error" role="alert">{error}</div> : null}
                {message ? <div className="mp-callout mp-callout-ok" role="status">{message}</div> : null}

                <div className="mp-table-scroll">
                    <table className="mp-table mp-senders-table">
                        <thead>
                            <tr>
                                <th>Активен</th>
                                <th>Отправитель</th>
                                <th>Приоритет</th>
                                <th title="Прибыль на единицу в режиме «от безубыточности». Пусто — прибыль по умолчанию аккаунта.">Прибыль, грн</th>
                                <th title="Цена = РРЦ поставщика">РРЦ</th>
                                <th title="Цена = закупка + розничная наценка поставщика, без формулы безубыточности. Действует только на этой площадке.">Демпинг</th>
                                <th>ТТН</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const name = row.supplier_name || row.supplier_code;
                                const ttn = effectiveTtn(row);
                                return (
                                    <tr key={row.supplier_code}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                aria-label={`Включить ${name}`}
                                                checked={Boolean(row.is_active)}
                                                disabled={disabled}
                                                onChange={(event) => change(row.supplier_code, "is_active", event.target.checked)}
                                            />
                                        </td>
                                        <td>
                                            <strong>{name}</strong>
                                            <div className="mp-muted">{row.supplier_code}</div>
                                            {row.schedule_blocked_now ? (
                                                <div className="mp-pill mp-pill-warn">
                                                    Недоступен по расписанию{row.schedule_window ? `: ${row.schedule_window}` : ""}
                                                </div>
                                            ) : null}
                                            {!row.supplier_active ? (
                                                <div className="mp-muted">Отключён в основном контуре</div>
                                            ) : null}
                                            {row.extra_markup_active ? (
                                                <div className="mp-pill mp-pill-warn" title="Доп. наценка ложится сверху прибыли">
                                                    Есть старая доп. наценка
                                                </div>
                                            ) : null}
                                        </td>
                                        <td>
                                            <NumberCell
                                                row={row}
                                                field="priority_override"
                                                label="Приоритет"
                                                placeholder={String(row.inherited_priority ?? "")}
                                                disabled={disabled}
                                                onChange={change}
                                            />
                                        </td>
                                        <td>
                                            <NumberCell
                                                row={row}
                                                field="min_markup_threshold"
                                                label="Прибыль"
                                                placeholder={profitPlaceholder}
                                                step="0.01"
                                                disabled={disabled}
                                                onChange={change}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                aria-label={`РРЦ: ${name}`}
                                                checked={Boolean(row.is_rrp)}
                                                disabled={disabled}
                                                onChange={(event) => change(row.supplier_code, "is_rrp", event.target.checked)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                aria-label={`Демпинг: ${name}`}
                                                checked={Boolean(row.dumping_mode)}
                                                disabled={disabled}
                                                onChange={(event) => change(row.supplier_code, "dumping_mode", event.target.checked)}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="mp-select"
                                                aria-label={`ТТН: ${name}`}
                                                value={row.order_ttn_mode || ""}
                                                disabled={disabled}
                                                onChange={(event) => change(row.supplier_code, "order_ttn_mode", event.target.value || null)}
                                            >
                                                <option value="">Как у поставщика ({TTN_LABELS[row.supplier_ttn_mode] || TTN_LABELS.auto})</option>
                                                <option value="auto">Автоматически</option>
                                                <option value="manual_later">Позже вручную</option>
                                            </select>
                                            {platform === "kasta" && ttn === "manual_later" ? (
                                                <div className="mp-field-hint">Заказ Kasta не обрабатывается автоматически — ведётся вручную</div>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {!rows.length && !loading ? <p className="mp-muted">Отправители не найдены.</p> : null}
                </div>
            </section>
        </div>
    );
}
